import { useEffect, useState } from 'react';
import { BadgeCheck, Banknote, ShieldCheck, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { SectionLabel, Reveal } from '../components/Section';
import { formatIST } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Payouts() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      axios
        .get(`${API}/payouts`)
        .then((r) => {
          if (alive) setData(r.data);
        })
        .catch(() => {
          if (alive) setData({ payouts: [], total_paid: 0, count: 0 });
        });
    load();
    const i = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, []);

  const payouts = data?.payouts || [];

  return (
    <main className="min-h-screen" data-testid="payouts-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <SectionLabel testid="payouts-label">Payout Proof</SectionLabel>
        <Reveal>
          <h1 className="mt-5 font-display text-white text-4xl sm:text-6xl">
            Real players. <span className="text-[#00c853]">Real payouts.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-4 text-[#9e9e9e] text-sm sm:text-base max-w-lg">
            Every approved withdrawal, published publicly. Names and IDs are masked — player privacy is never
            compromised.
          </p>
        </Reveal>

        {data && data.count > 0 && (
          <Reveal delay={0.1}>
            <div className="mt-10 grid grid-cols-2 gap-4 max-w-xl" data-testid="payouts-stats">
              <div className="card-surface p-6">
                <p className="text-3xl font-black italic text-[#00c853] flex items-center gap-2">
                  <Banknote className="w-6 h-6" />₹{Number(data.total_paid || 0).toLocaleString('en-IN')}
                </p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a8a8a]">Total Paid Out</p>
              </div>
              <div className="card-surface p-6">
                <p className="text-3xl font-black italic text-white flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-[#e8173a]" />
                  {data.count}
                </p>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a8a8a]">Approved Payouts</p>
              </div>
            </div>
          </Reveal>
        )}

        {data === null ? (
          <div className="mt-10 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-[#1a1f2e] animate-pulse" data-testid={`payout-skeleton-${i}`} />
            ))}
          </div>
        ) : payouts.length === 0 ? (
          <Reveal>
            <div className="mt-10 card-surface p-12 text-center" data-testid="payouts-empty">
              <span className="mx-auto w-14 h-14 rounded-xl bg-[#00c853]/10 border border-[#00c853]/30 flex items-center justify-center">
                <ShieldCheck className="w-7 h-7 text-[#00c853]" />
              </span>
              <p className="mt-5 text-white font-bold text-lg">Payout proofs publishing soon</p>
              <p className="mt-2 text-sm text-[#8a8a8a] max-w-sm mx-auto">
                Every approved withdrawal will appear here automatically — amounts verified, identities masked.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="mt-10 space-y-3" data-testid="payouts-list">
            {payouts.map((p, i) => (
              <Reveal key={`${p.uid}-${p.approved_at}-${i}`} delay={Math.min(i, 8) * 0.05}>
                <div
                  className="card-surface card-surface-hover p-5 flex items-center gap-4"
                  data-testid={`payout-row-${i}`}
                >
                  <span className="w-11 h-11 rounded-lg bg-[#00c853]/10 border border-[#00c853]/30 flex items-center justify-center shrink-0">
                    <BadgeCheck className="w-5 h-5 text-[#00c853]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate">{p.name}</p>
                    <p className="text-[11px] text-[#8a8a8a] font-mono mt-0.5">
                      UID: {p.uid}
                      {p.approved_at && <span className="font-sans"> • {formatIST(p.approved_at)}</span>}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[#00c853] font-extrabold italic text-lg leading-none">
                      ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                    </p>
                    <span className="inline-block mt-1.5 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#00c853]/60 text-[#00c853]">
                      Approved
                    </span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
